/**
 * Resolve the allow-list for postMessage origins that the /preview route
 * accepts from the parent.
 *
 * Production: comma-separated `VITE_DESIGNER_ORIGINS` env var, e.g.
 *   `https://designer.viedoc.com,https://designer.staging.viedoc.com`.
 *
 * Dev fallback (env var empty / absent): accept `http(s)?://localhost:<any-port>`
 * and `http(s)?://designer.localhost`. This keeps local Vite + a hand-launched
 * Designer instance working without per-developer env files. IIS Express
 * defaults to HTTPS for ASP.NET projects, so both schemes must be allowed
 * in dev — production locks this down via VITE_DESIGNER_ORIGINS.
 *
 * See docs/features/2-preview-route.md (postMessage protocol).
 */

const DEV_LOCALHOST_REGEX = /^https?:\/\/localhost(?::\d+)?$/;
const DEV_DESIGNER_LOCALHOST_REGEX = /^https?:\/\/designer\.localhost$/;

export function getAllowedOrigins() {
    // NOTE: `import.meta.env.X` must be a literal MemberExpression chain for
    // Vite's dev-mode transform to substitute the value. Optional chaining
    // between `meta` and `env` breaks the pattern match and leaves the access
    // untransformed (result: undefined at runtime). Keep it literal here.
    //
    // And never reference `import.meta.env` on its own. A bare reference makes
    // Vite inline the WHOLE env object into the production bundle, every
    // VITE_* key included, whether or not the app reads it.
    const raw = import.meta.env.VITE_DESIGNER_ORIGINS || "";

    return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * @param {string} origin — value of `event.origin` (already validated by the
 *   browser to be a serialized origin string)
 * @returns {boolean}
 */
export function isAllowedOrigin(origin) {
    if (!origin || origin === "null") return false;

    const configured = getAllowedOrigins();
    if (configured.length > 0) {
        return configured.includes(origin);
    }

    // Dev / E2E-only localhost fallback. A production build MUST set
    // VITE_DESIGNER_ORIGINS; the fallback is gated so a prod deploy that forgot
    // to wire the env var fails CLOSED (rejects every origin) rather than
    // silently trusting any localhost origin.
    //
    // Two gates open it:
    //   - `import.meta.env.DEV` — the Vite dev server + a hand-launched Designer.
    //   - `VITE_PREVIEW_ALLOW_LOCALHOST === "true"` — the E2E harness, which
    //     serves a *production* build via nginx on a dynamic localhost port and
    //     posts render-result same-origin, so it can't use a fixed
    //     VITE_DESIGNER_ORIGINS. Only `npm run build:e2e` sets this; real
    //     production never does, so prod stays fail-closed.
    // Both accesses are literal MemberExpression chains (see the note in
    // getAllowedOrigins) so Vite statically substitutes them at build time.
    const allowLocalhost =
        import.meta.env.DEV ||
        import.meta.env.VITE_PREVIEW_ALLOW_LOCALHOST === "true";
    if (!allowLocalhost) return false;

    if (DEV_LOCALHOST_REGEX.test(origin)) return true;
    if (DEV_DESIGNER_LOCALHOST_REGEX.test(origin)) return true;

    return false;
}
